import React, { useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { API_BASE_URL } from '../components/config/api';
import { useToast } from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import { LoadFailed, SwitchRow } from './settings/instantSettings';
import { SheetField } from './settings/sheet';
import {
  ASIDE_BTN, Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, ListPage, ListToolbar,
  PRIMARY_BTN, SELECT_CLS, StatusChip,
} from './shared/listPage';
import {
  Plus,
  Trash2,
  Wifi,
  Terminal,
  Key,
  Loader2,
  AlertTriangle
} from 'lucide-react';

/**
 * Ταμείο → «POS Τερματικά» στη διάταξη «Πίνακας» (14/10/2026): τα τερματικά του καταστήματος σε πίνακα
 * (κλικ στη γραμμή = συρτάρι με τη φόρμα, «Διαγραφή» κάτω αριστερά), από κάτω οι πάροχοι με την
 * κατάσταση των στοιχείων σύνδεσης και τη «Δοκιμή σύνδεσης». Ίδια endpoints, σώματα και κανόνες με πριν:
 *  - στοιχεία σύνδεσης ανά πάροχο (migration 269): η Viva (πάροχος καταστήματος) ορίζεται εδώ, με
 *    Client ID / Client Secret και κενό Secret = μένει ο αποθηκευμένος· των παρόχων πλατφόρμας τα ορίζει η
 *    υποστήριξη (μόνο η γραμμή κατάστασης)· άγνωστη κατάσταση (σφάλμα/403) ≠ «δεν έχουν οριστεί»·
 *  - τερματικό: POST/PUT με τα ίδια πεδία (API key / Result ID / Live-Demo ανά πάροχο), DELETE με επιβεβαίωση.
 */

type CredentialsScope = 'platform' | 'store';

interface POSProvider {
  id: number;
  code: string;
  name: string;
  is_active: boolean;
  credentials_scope?: CredentialsScope | null;
}

// Από πού λύνονται τα στοιχεία σύνδεσης κάθε παρόχου για το κατάστημα (GET /pos/credentials,
// migration 269): ίδιος κανόνας με την πληρωμή. username μόνο σε διαχειριστή, μόνο για πάροχο
// καταστήματος (Viva)· κωδικός ποτέ.
interface POSCredentialStatus {
  provider_id: number;
  provider_code: string;
  provider_name: string;
  credentials_scope: CredentialsScope;
  source: 'store' | 'platform' | null;
  username?: string;
  password_masked?: string | null;
}

interface POSTerminal {
  id: number;
  provider_id: number;
  provider_code: string;
  provider_name: string;
  terminal_id: string;
  terminal_name: string;
  terminal_api_key?: string;
  terminal_result_id?: string;
  status: string;
  is_active: boolean;
  is_default: boolean;
}

type LoadState = 'ready' | 'error' | 'forbidden';

const EMPTY_TERMINAL_FORM = {
  providerId: '',
  terminalId: '',
  terminalName: '',
  terminalApiKey: '',
  terminalResultId: '',
  status: 'live',
  isDefault: false
};

// Πριν το migration 269 το credentials_scope λείπει: ίδια προεπιλογή με τον server (Viva = κατάστημα).
const credentialsScopeOf = (p?: { code?: string; credentials_scope?: string | null } | null): CredentialsScope =>
  p?.credentials_scope === 'store' || p?.credentials_scope === 'platform'
    ? p.credentials_scope
    : ((p?.code || '').toLowerCase() === 'viva' ? 'store' : 'platform');

const POSTerminals: React.FC = () => {
  const { t } = useTranslation(['settings', 'common', 'posTerminals', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const uid = useId();
  const terminalFormId = `${uid}-terminal`;
  const credentialsFormId = `${uid}-credentials`;
  const providersTitleId = `${uid}-providers`;
  const [providers, setProviders] = useState<POSProvider[]>([]);
  // null = άγνωστο (σφάλμα δικτύου/δικαιώματος): καμία γραμμή κατάστασης αντί για ψεύτικο «δεν έχουν οριστεί»
  const [credentialStatus, setCredentialStatus] = useState<POSCredentialStatus[] | null>(null);
  const [terminals, setTerminals] = useState<POSTerminal[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState<LoadState>('ready');
  const [confirmModal, setConfirmModal] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => {} });
  const [testingConnection, setTestingConnection] = useState<number | null>(null);

  // Συρτάρια (ως 14/10/2026 παράθυρα στη μέση)
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showTerminalModal, setShowTerminalModal] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState<POSTerminal | null>(null);
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Form data
  const [credentialForm, setCredentialForm] = useState({
    providerId: '',
    username: '',
    password: ''
  });

  const [terminalForm, setTerminalForm] = useState(EMPTY_TERMINAL_FORM);

  // Fetch POS providers
  const fetchProviders = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/pos/providers`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProviders(data);
      }
    } catch (error) {
      console.error('Failed to fetch POS providers:', error);
    }
  };

  // Fetch where each provider's credentials resolve for this store
  const fetchCredentials = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/credentials`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCredentialStatus(Array.isArray(data) ? data : null);
      } else {
        setCredentialStatus(null);
      }
    } catch (error) {
      console.error('Failed to fetch credentials:', error);
      setCredentialStatus(null);
    }
  };

  // Fetch store terminals
  const fetchTerminals = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/terminals`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTerminals(data);
        setLoadState('ready');
      } else {
        // Ως 14/10/2026 μια αποτυχία έδειχνε σιωπηλά «Δεν έχουν ρυθμιστεί POS Terminals».
        setLoadState(response.status === 403 ? 'forbidden' : 'error');
      }
    } catch (error) {
      console.error('Failed to fetch terminals:', error);
      setLoadState('error');
    } finally {
      setLoading(false);
    }
  };

  const loadAll = () => {
    fetchProviders();
    fetchCredentials();
    fetchTerminals();
  };

  useEffect(() => {
    if (store?.id) {
      loadAll();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.id]);

  const retryLoad = () => {
    setLoading(true);
    loadAll();
  };

  const statusFor = (providerId: number) => credentialStatus?.find(s => s.provider_id === providerId);
  const scopeFor = (providerId: number, code?: string) =>
    credentialsScopeOf(statusFor(providerId) || providers.find(p => p.id === providerId) || { code });

  // Μόνο οι πάροχοι καταστήματος (Viva) έχουν στοιχεία εδώ· των υπόλοιπων τα ορίζει η υποστήριξη.
  const storeScopeProviders = providers.filter(p => scopeFor(p.id, p.code) === 'store');
  const providerById = (id: number) => {
    const p = providers.find(x => x.id === id);
    if (p) return { id: p.id, code: p.code, name: p.name };
    const s = statusFor(id);
    return s ? { id: s.provider_id, code: s.provider_code, name: s.provider_name } : undefined;
  };
  const formProvider = credentialForm.providerId ? providerById(parseInt(credentialForm.providerId)) : undefined;
  const formStatus = formProvider ? statusFor(formProvider.id) : undefined;
  const formIsViva = (formProvider?.code || '').toLowerCase() === 'viva';
  const formHasSaved = formStatus?.source === 'store';

  const closeCredentialsModal = () => {
    setShowCredentialsModal(false);
    setCredentialForm({ providerId: '', username: '', password: '' });
  };

  // Το αποθηκευμένο Client ID προσυμπληρώνεται (το βλέπει μόνο διαχειριστής)· το Secret ποτέ.
  const credentialFormFor = (id?: number) => ({
    providerId: id ? id.toString() : '',
    username: (id && statusFor(id)?.username) || '',
    password: ''
  });

  const openCredentialsModal = (providerId?: number) => {
    const id = providerId ?? (storeScopeProviders.length === 1 ? storeScopeProviders[0].id : undefined);
    setCredentialForm(credentialFormFor(id));
    setShowCredentialsModal(true);
  };

  // Save credentials (μόνο πάροχος καταστήματος — ο server αρνείται τους υπόλοιπους)
  const handleSaveCredentials = async () => {
    if (!credentialForm.providerId || !credentialForm.username.trim() || (!credentialForm.password && !formHasSaved)) {
      showToast('error', t('settings:pos.credentialsRequired'));
      return;
    }
    setSavingCredentials(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          providerId: parseInt(credentialForm.providerId),
          username: credentialForm.username.trim(),
          password: credentialForm.password
        })
      });

      if (response.ok) {
        await fetchCredentials();
        closeCredentialsModal();
        showToast('success', t('settings:pos.saveSuccess'));
      } else {
        const err = await response.json().catch(() => ({}));
        showToast('error', err.message || err.error || t('posTerminals:form.httpError', { status: response.status }));
      }
    } catch (error) {
      console.error('Failed to save credentials:', error);
      showToast('error', t('settings:pos.saveError'));
    } finally {
      setSavingCredentials(false);
    }
  };

  const closeTerminalModal = () => {
    setShowTerminalModal(false);
    setEditingTerminal(null);
    setTerminalForm(EMPTY_TERMINAL_FORM);
  };

  const openNewTerminal = () => {
    setEditingTerminal(null);
    setTerminalForm(EMPTY_TERMINAL_FORM);
    setShowTerminalModal(true);
  };

  const openTerminal = (terminal: POSTerminal) => {
    setEditingTerminal(terminal);
    setTerminalForm({
      providerId: terminal.provider_id.toString(),
      terminalId: terminal.terminal_id,
      terminalName: terminal.terminal_name,
      terminalApiKey: terminal.terminal_api_key || '',
      terminalResultId: terminal.terminal_result_id || '',
      status: terminal.status,
      isDefault: terminal.is_default
    });
    setShowTerminalModal(true);
  };

  // Save terminal
  const handleSaveTerminal = async () => {
    try {
      const url = editingTerminal
        ? `${API_BASE_URL}/api/stores/${store?.id}/pos/terminals/${editingTerminal.id}`
        : `${API_BASE_URL}/api/stores/${store?.id}/pos/terminals`;

      const method = editingTerminal ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({
          providerId: parseInt(terminalForm.providerId),
          terminalId: terminalForm.terminalId,
          terminalName: terminalForm.terminalName,
          terminalApiKey: terminalForm.terminalApiKey || null,
          terminalResultId: terminalForm.terminalResultId || null,
          status: terminalForm.status,
          isDefault: terminalForm.isDefault
        })
      });

      if (response.ok) {
        await fetchTerminals();
        closeTerminalModal();
        showToast('success', t('settings:pos.saveSuccess'));
      } else {
        const err = await response.json().catch(() => ({}));
        showToast('error', err.error || err.message || t('posTerminals:form.httpError', { status: response.status }));
      }
    } catch (error) {
      console.error('Failed to save terminal:', error);
      showToast('error', t('posTerminals:form.networkError'));
    }
  };

  // Delete terminal (από το συρτάρι του· μετά τη διαγραφή το συρτάρι κλείνει)
  const handleDeleteTerminal = (terminalId: number) => {
    setConfirmModal({
      open: true,
      title: t('settings:pos.deleteTerminal'),
      message: t('settings:pos.deleteConfirm'),
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, open: false }));
        try {
          const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/terminals/${terminalId}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
            }
          });

          if (response.ok) {
            showToast('success', t('settings:pos.deleteSuccess'));
            closeTerminalModal();
            await fetchTerminals();
          } else {
            showToast('error', t('settings:pos.deleteError'));
          }
        } catch (error) {
          console.error('Failed to delete terminal:', error);
          showToast('error', t('settings:pos.deleteError'));
        }
      }
    });
  };

  // Test connection
  const handleTestConnection = async (providerId: number) => {
    setTestingConnection(providerId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/stores/${store?.id}/pos/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
        },
        body: JSON.stringify({ providerId })
      });

      const result = await response.json();

      if (result.success) {
        showToast('success', t('settings:pos.connectionSuccess'));
      } else {
        showToast('error', t('settings:pos.connectionError', { message: result.message || t('settings:pos.unknownError') }));
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      showToast('error', t('settings:pos.testConnectionError'));
    } finally {
      setTestingConnection(null);
    }
  };

  // Group terminals by provider (με την ταυτότητα, όχι το όνομα — η σειρά του server μένει)
  const providerGroups = terminals.reduce((acc, terminal) => {
    let group = acc.find(g => g.providerId === terminal.provider_id);
    if (!group) {
      group = { providerId: terminal.provider_id, providerName: terminal.provider_name, providerCode: terminal.provider_code, terminals: [] };
      acc.push(group);
    }
    group.terminals.push(terminal);
    return acc;
  }, [] as { providerId: number; providerName: string; providerCode: string; terminals: POSTerminal[] }[]);

  // Στον πίνακα με τη σειρά των παλιών καρτών: ανά πάροχο, όπως τους έφερε ο server.
  const rows = providerGroups.flatMap(g => g.terminals);

  // Check if provider needs API key (all except Viva and MyPay)
  const providerNeedsApiKey = (providerCode: string) => {
    return !['viva', 'mypay'].includes(providerCode.toLowerCase());
  };

  // Check if provider needs result ID (all except MyPay)
  const providerNeedsResultId = (providerCode: string) => {
    return providerCode.toLowerCase() !== 'mypay';
  };

  // «Στοιχεία σύνδεσης: πλατφόρμας ✓» / «καταστήματος ✓» / «Δεν έχουν οριστεί …» κάτω από τον πάροχο.
  const renderCredentialLine = (providerId: number, providerCode: string) => {
    if (!credentialStatus) return null;
    const scope = scopeFor(providerId, providerCode);
    const source = statusFor(providerId)?.source ?? null;
    const text = source === 'platform'
      ? t('settings:pos.credentialsPlatform')
      : source === 'store'
        ? t('settings:pos.credentialsStore')
        : scope === 'store'
          ? t('settings:pos.credentialsMissingStore')
          : t('settings:pos.credentialsMissing');
    // Όχι .xs-row-sub: το sheet.css φορτώνεται μετά το Tailwind και το γκρι του θα έσβηνε το πράσινο/πορτοκαλί.
    return (
      <p
        className={`mt-1 flex items-center gap-1.5 text-[13px] leading-[19px] ${source ? 'text-emerald-700 dark:text-emerald-400' : 'text-amber-800 dark:text-amber-300'}`}
        data-testid={`pos-credentials-${providerId}`}
      >
        {!source && <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />}
        {text}
      </p>
    );
  };

  const columns: Column<POSTerminal>[] = [
    {
      key: 'name',
      header: t('posTerminals:columns.name'),
      width: 'minmax(170px, 1.4fr)',
      stack: 'title',
      render: x => <span className="font-medium">{x.terminal_name}</span>,
    },
    {
      key: 'terminal_id',
      header: t('posTerminals:columns.terminalId'),
      width: 'minmax(120px, 1fr)',
      render: x => <span className="font-mono text-[13px] text-t2">{x.terminal_id}</span>,
      stackRender: x => `ID ${x.terminal_id}`,
    },
    {
      key: 'provider',
      header: t('posTerminals:columns.provider'),
      width: 'minmax(120px, 1fr)',
      render: x => x.provider_name,
    },
    {
      key: 'status',
      header: t('posTerminals:columns.status'),
      width: 'minmax(200px, 1.3fr)',
      render: x => (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <StatusChip tone={x.is_active ? 'ok' : 'muted'}>{x.is_active ? t('common:active') : t('common:inactive')}</StatusChip>
          {x.is_default && <StatusChip tone="brand">{t('settings:pos.defaultBadge')}</StatusChip>}
          {x.status === 'demo' && <StatusChip tone="warn">{t('posTerminals:demo')}</StatusChip>}
        </span>
      ),
    },
  ];

  const editingProviderCode = providers.find(p => p.id.toString() === terminalForm.providerId)?.code || '';

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.register')}
      title={t('settings:nav.posTerminals')}
      subtitle={t('settings:pos.subtitle')}
      testId="pos-terminals"
    >
      <ListToolbar
        actions={(
          <>
            {storeScopeProviders.length > 0 && (
              <button type="button" onClick={() => openCredentialsModal()} className={GHOST_BTN}>
                <Key className="h-4 w-4" aria-hidden="true" />
                {storeScopeProviders.length === 1
                  ? t('settings:pos.credentialsTitleFor', { provider: storeScopeProviders[0].name })
                  : t('settings:pos.manageCredentials')}
              </button>
            )}
            <button type="button" onClick={openNewTerminal} className={PRIMARY_BTN}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('settings:pos.addTerminal')}
            </button>
          </>
        )}
        summary={loadState === 'ready' && !loading ? t('posTerminals:count', { count: rows.length }) : null}
      />

      {loadState !== 'ready' ? (
        <LoadFailed
          message={loadState === 'forbidden' ? t('posTerminals:forbidden') : t('posTerminals:loadFailed')}
          onRetry={retryLoad}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={x => x.id}
          onRowClick={openTerminal}
          selectedKey={showTerminalModal ? editingTerminal?.id ?? null : null}
          loading={loading}
          skeletonRows={3}
          rowLabel={x => t('posTerminals:rowLabel', { name: x.terminal_name, provider: x.provider_name })}
          empty={(
            <EmptyState
              icon={Terminal}
              title={t('settings:pos.noTerminals')}
              hint={t('settings:pos.noTerminalsHint')}
              action={(
                <button type="button" onClick={openNewTerminal} className={PRIMARY_BTN}>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  {t('settings:pos.addTerminal')}
                </button>
              )}
            />
          )}
          testId="pos-table"
        />
      )}

      {/* Πάροχοι των τερματικών: κατάσταση στοιχείων σύνδεσης, «Ορισμός/Αλλαγή» (Viva), «Δοκιμή σύνδεσης». */}
      {loadState === 'ready' && providerGroups.length > 0 && (
        <section className="xs-block mt-8" aria-labelledby={providersTitleId} data-testid="pos-providers">
          <h2 id={providersTitleId} className="xs-sec-title"><span>{t('posTerminals:providers.title')}</span></h2>
          <p className="xs-sec-hint">{t('posTerminals:providers.hint')}</p>
          <div className="mt-5">
            {providerGroups.map(({ providerId, providerName, providerCode }) => {
              const scope = scopeFor(providerId, providerCode);
              const source = statusFor(providerId)?.source;
              // Όχι .xs-row: εκείνη δεν αναδιπλώνεται, και στο κινητό δύο κουμπιά θα πατούσαν πάνω στο όνομα.
              return (
                <div
                  key={providerId}
                  className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 py-4 border-t border-line first:border-t-0 first:pt-0"
                  data-pos-provider={providerId}
                >
                  <div className="min-w-[13rem] flex-1">
                    <h3 className="text-sm font-semibold text-t1">{providerName}</h3>
                    {renderCredentialLine(providerId, providerCode)}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {credentialStatus && scope === 'store' && (
                      <button type="button" onClick={() => openCredentialsModal(providerId)} className={ASIDE_BTN}>
                        {source ? t('settings:pos.editCredentials') : t('settings:pos.setCredentials')}
                      </button>
                    )}
                    {source && (
                      <button
                        type="button"
                        onClick={() => handleTestConnection(providerId)}
                        disabled={testingConnection === providerId}
                        className={ASIDE_BTN}
                      >
                        {testingConnection === providerId
                          ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          : <Wifi className="h-4 w-4" aria-hidden="true" />}
                        {t('settings:pos.testConnection')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Στοιχεία σύνδεσης — μόνο πάροχοι καταστήματος (Viva: Client ID / Client Secret του merchant) */}
      <DetailPanel
        open={showCredentialsModal}
        onClose={closeCredentialsModal}
        title={formProvider ? t('settings:pos.credentialsTitleFor', { provider: formProvider.name }) : t('settings:pos.credentialsTitle')}
        footer={(
          <>
            <button type="button" onClick={closeCredentialsModal} className={GHOST_BTN}>
              {t('common:cancel')}
            </button>
            <button type="submit" form={credentialsFormId} disabled={savingCredentials || !credentialForm.providerId} className={PRIMARY_BTN}>
              {savingCredentials && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              {t('common:save')}
            </button>
          </>
        )}
        testId="pos-credentials-form"
      >
        <form
          id={credentialsFormId}
          noValidate
          className="space-y-5"
          onSubmit={e => { e.preventDefault(); if (!savingCredentials && credentialForm.providerId) handleSaveCredentials(); }}
        >
          {storeScopeProviders.length > 1 && (
            <SheetField label={t('settings:pos.provider')} htmlFor="pos-credential-provider">
              <select
                id="pos-credential-provider"
                value={credentialForm.providerId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setCredentialForm(credentialFormFor(parseInt(e.target.value) || undefined))}
                className={SELECT_CLS}
              >
                <option value="">{t('settings:pos.selectProvider')}</option>
                {storeScopeProviders.map((provider) => (
                  <option key={provider.id} value={provider.id.toString()}>
                    {provider.name}
                  </option>
                ))}
              </select>
            </SheetField>
          )}

          {formIsViva && <p className="text-sm text-t3">{t('settings:pos.vivaHint')}</p>}

          <SheetField label={formIsViva ? t('settings:pos.clientId') : t('settings:pos.username')} htmlFor="pos-credential-username">
            <input
              id="pos-credential-username"
              type="text"
              value={credentialForm.username}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCredentialForm({...credentialForm, username: e.target.value})}
              placeholder={formIsViva ? t('settings:pos.clientIdPlaceholder') : t('settings:pos.usernamePlaceholder')}
              autoComplete="one-time-code"
              className={INPUT_CLS}
            />
          </SheetField>

          <SheetField label={formIsViva ? t('settings:pos.clientSecret') : t('settings:pos.password')} htmlFor="pos-credential-secret">
            <input
              id="pos-credential-secret"
              type="password"
              value={credentialForm.password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCredentialForm({...credentialForm, password: e.target.value})}
              placeholder={formHasSaved
                ? t('settings:pos.secretKeepPlaceholder')
                : (formIsViva ? t('settings:pos.clientSecretPlaceholder') : t('settings:pos.passwordPlaceholder'))}
              autoComplete="one-time-code"
              className={INPUT_CLS}
            />
          </SheetField>
        </form>
      </DetailPanel>

      {/* Τερματικό: νέο ή υπάρχον */}
      <DetailPanel
        open={showTerminalModal}
        onClose={() => {
          // Το παράθυρο επιβεβαίωσης ζει μέσα στο συρτάρι: το Esc κλείνει ΠΡΩΤΑ αυτό.
          if (confirmModal.open) setConfirmModal(prev => ({ ...prev, open: false }));
          else closeTerminalModal();
        }}
        title={editingTerminal ? t('settings:pos.editTerminal') : t('settings:pos.addTerminal')}
        subtitle={editingTerminal ? `${editingTerminal.terminal_name} · ${editingTerminal.provider_name}` : undefined}
        footerStart={editingTerminal ? (
          <button type="button" onClick={() => handleDeleteTerminal(editingTerminal.id)} className={DANGER_GHOST_BTN}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            {t('common:delete')}
          </button>
        ) : undefined}
        footer={(
          <>
            <button type="button" onClick={closeTerminalModal} className={GHOST_BTN}>
              {t('common:cancel')}
            </button>
            <button type="submit" form={terminalFormId} className={PRIMARY_BTN}>
              {editingTerminal ? t('common:update') : t('common:add')}
            </button>
          </>
        )}
        testId="pos-terminal-form"
      >
        <form id={terminalFormId} noValidate className="space-y-5" onSubmit={e => { e.preventDefault(); handleSaveTerminal(); }}>
          <SheetField label={t('settings:pos.provider')} htmlFor="pos-terminal-provider">
            <select
              id="pos-terminal-provider"
              value={terminalForm.providerId}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTerminalForm({...terminalForm, providerId: e.target.value})}
              disabled={!!editingTerminal}
              className={SELECT_CLS}
            >
              <option value="">{t('settings:pos.selectProvider')}</option>
              {providers.map((provider) => (
                <option key={provider.id} value={provider.id.toString()}>
                  {provider.name}
                </option>
              ))}
            </select>
          </SheetField>

          <div className="xs-grid2">
            <SheetField label={t('posTerminals:form.terminalId')} htmlFor="pos-terminal-id">
              <input
                id="pos-terminal-id"
                type="text"
                value={terminalForm.terminalId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerminalForm({...terminalForm, terminalId: e.target.value})}
                placeholder={t('posTerminals:form.terminalIdPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                className={`${INPUT_CLS} font-mono`}
              />
            </SheetField>

            <SheetField label={t('settings:pos.terminalName')} htmlFor="pos-terminal-name">
              <input
                id="pos-terminal-name"
                type="text"
                value={terminalForm.terminalName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerminalForm({...terminalForm, terminalName: e.target.value})}
                placeholder={t('posTerminals:form.terminalNamePlaceholder')}
                autoComplete="off"
                className={INPUT_CLS}
              />
            </SheetField>
          </div>

          {terminalForm.providerId && providerNeedsApiKey(editingProviderCode) && (
            <SheetField label={t('posTerminals:form.apiKey')} htmlFor="pos-terminal-api-key">
              <input
                id="pos-terminal-api-key"
                type="text"
                value={terminalForm.terminalApiKey}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerminalForm({...terminalForm, terminalApiKey: e.target.value})}
                placeholder={t('posTerminals:form.apiKeyPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                className={`${INPUT_CLS} font-mono`}
              />
            </SheetField>
          )}

          {terminalForm.providerId && providerNeedsResultId(editingProviderCode) && (
            <SheetField label={t('posTerminals:form.resultId')} htmlFor="pos-terminal-result-id">
              <input
                id="pos-terminal-result-id"
                type="text"
                value={terminalForm.terminalResultId}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTerminalForm({...terminalForm, terminalResultId: e.target.value})}
                placeholder={t('posTerminals:form.resultIdPlaceholder')}
                autoComplete="off"
                spellCheck={false}
                className={`${INPUT_CLS} font-mono`}
              />
            </SheetField>
          )}

          {terminalForm.providerId && editingProviderCode === 'mypay' && (
            <SheetField label={t('posTerminals:form.mode')} htmlFor="pos-terminal-mode">
              <select
                id="pos-terminal-mode"
                value={terminalForm.status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTerminalForm({...terminalForm, status: e.target.value})}
                className={`${SELECT_CLS} sm:max-w-[16rem]`}
              >
                <option value="live">{t('posTerminals:form.live')}</option>
                <option value="demo">{t('posTerminals:form.demo')}</option>
              </select>
            </SheetField>
          )}

          <div className="pt-1">
            <SwitchRow
              title={t('settings:pos.setDefault')}
              checked={terminalForm.isDefault}
              onChange={next => setTerminalForm({...terminalForm, isDefault: next})}
            />
          </div>
        </form>

        {/* Μέσα στο συρτάρι: πάνω από αυτό και με το Tab να φτάνει στα κουμπιά του. */}
        <ConfirmModal
          isOpen={confirmModal.open}
          title={confirmModal.title}
          message={confirmModal.message}
          onConfirm={confirmModal.onConfirm}
          onCancel={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        />
      </DetailPanel>
    </ListPage>
  );
};

export default POSTerminals;
